import { h } from 'preact';
import Store from 'store/store';
import { distanceFrom, geoFromIP } from 'utils/geo';
import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { getStudioConfig } from 'utils/studioConfig';
import { joinClasses, sortObjectByOrder } from 'utils/utils';
import { getPages, getShowtimesForCMSPreview } from 'components/editor/movies/utils';

import HomeEditor from 'components/editor/movies/home/home';
import SelectableSection from 'components/editor/selectableSection';
import SocialsEditor from 'components/editor/movies/home/shared/socialsEditor';
import ToggleField from 'components/editor/settings/toggleField';
import SimpleField from 'components/editor/settings/simpleField';
import LinkField from 'components/editor/settings/linkField';
import Button from 'components/button/button';


import s from 'components/editor/movies/home/preview/homePreview.sss';

export default class PreviewHomeEditor extends HomeEditor {
	s = s;

	constructor(props) {
		super(props);

		this.setState({
			activeTab: this.getAvailableTabs(props)?.[0]?.view || null,
		});

		this.onTabClick = this.onTabClick.bind(this);
		this.onResetSecondaryButtonClick = this.onResetSecondaryButtonClick.bind(this);
		this.onResetSectionsClick = this.onResetSectionsClick.bind(this);
		this.renderTheater = this.renderTheater.bind(this);

		// Initialize secondaryButton if not set
		const secondaryButtonPath = '/content/pages/home/previewPanelSecondaryButton';
		let secondaryButton = jsonPointer.get(props.data, secondaryButtonPath);
		if (!secondaryButton) {
			secondaryButton = { enabled: false, label: '', path: '' };
			jsonPointer.setDeep(Store.get().data, `${props.dataRoot}${secondaryButtonPath}`, secondaryButton);
		}
	}

	componentWillReceiveProps(nextProps, nextState) {
		super.componentWillReceiveProps?.(nextProps, nextState);
		if (nextProps.data !== this.props.data) {
			const tabs = this.getAvailableTabs(nextProps);
			const selectedTabs = nextProps.data?.content?.pages?.home?.previewPanelTabs;
			const activeTabs = tabs.filter(tab => selectedTabs?.[tab.view] ?? tab.defaultActive);
			if (!activeTabs.some(tab => tab.view === this.state.activeTab)) {
				this.setState({ activeTab: activeTabs?.[0]?.view || null });
			}
		}
	}

	async updateShowtimes(props = this.props) {
		const showtimesId = props.data?.content?.showtimesId;
		this.setState({ loadedShowtimesId: showtimesId, showtimesLoading: 'loading' });
		if (!showtimesId) {
			return;
		}
		try {
			const geo = await geoFromIP();
			this.setState({ geo });
		} catch {}
		try {
			const { data, dates } = await getShowtimesForCMSPreview(showtimesId, this.state.geo);
			this.setState({
				showtimesLoading: 'loaded',
				showtimes: data,
				showtimesDates: dates,
			});
		} catch (e) {
			this.setState({ showtimesLoading: 'error', showtimes: null, showtimesDates: null });
		}
	}

	onTabClick(e) {
		const tab = e.currentTarget.dataset.tab;
		this.setState({ activeTab: tab });
		if (tab === 'boltShowtimes') {
			this.updateShowtimes();
		}
	}

	getAvailableTabs(props = this.props) {
		const pages = props.data?.content?.pages;
		const homeEntSections = pages?.homeEnt?.active && sortObjectByOrder(pages?.homeEnt?.sections, (val, key) => [key, val]) || [];
		const hasShowtimes = pages?.showtimes?.active;
		const tabs = homeEntSections.map(([id, section], index) => ({
			name: pages?.homeEnt?.filters?.[id]?.title || section.title,
			view: `boltHomeEnt:${id}`,
			defaultActive: index === 0 || (index === 1 && !hasShowtimes),
		}));
		if (hasShowtimes) {
			tabs.push({
				name: 'Showtimes',
				view: 'boltShowtimes',
				defaultActive: true,
			});
		}
		return tabs;
	}

	renderVisual() {
		const config = getStudioConfig();

		return (
			<SelectableSection {...this.props} class={this.s.previewWrapper} section="/">
				{this.renderBackground()}
				{this.renderHero()}
				{config.showSocials && <SocialsEditor {...this.props} class={this.s.socials} key="socials" />}
			</SelectableSection>
		);
	}

	getMerchantLogo(item) {
		if (item.useCustomMerchant) {
			return item?.customMerchant?.image || null;
		}

		if (!item?.merchant) {
			return null;
		}

		const logoMode = 'light';
		return `${__SHARED_MEDIA_ROOT__}/images/boltHomeEntMerchants/${logoMode}/${item.merchant}_${logoMode}.png`;
	}

	renderTheater(theater) {
		if (!theater?.screenings) return;

		const logo = theater.chain?.logo || 'default.png';
		const address = [
			theater.address?.intl?.street,
			theater.address?.intl?.city,
		].filter(Boolean).join(', ');
		const screening = theater.screenings?.[0];
		const extra = theater.screenings.length - 1;

		return (
			<div class={this.s.theater} key={theater.id}>
				<div class={this.s.theaterHeading}>
					<div class={this.s.icon}>
						<img src={`${__SHARED_MEDIA_ROOT__}/images/logos/light/${logo}`} alt="" />
					</div>
					<div class={this.s.name}>{theater.name}</div>
					<div class={this.s.location}>
						<div class={this.s.address}>{address}</div>
						<div class={this.s.distance}>{distanceFrom(this.state.geo, theater, 'mi', 1)}mi</div>
					</div>
				</div>
				<div class={this.s.screenings}>
					<div class={this.s.formatName}>{screening?.format}</div>
					<div class={this.s.screening}>{screening?.time}</div>
					{extra > 0 && <div class={this.s.extraScreenings}>{extra} additional showtimes at this theater</div>}
				</div>
			</div>
		);
	}

	renderHeroContent() {
		const props = this.props;
		const state = this.state;
		const pages = props.data?.content?.pages;

		const showtimesActive = !!pages?.showtimes?.active;
		const homeEntActive = !!pages?.homeEnt?.active;

		if (!showtimesActive && !homeEntActive) {
			return (
				<div class={this.s.warning}>Enable Showtimes or Home Entertainment to see preview tabs</div>
			);
		}

		const secondaryButton = pages?.home?.previewPanelSecondaryButton || {};
		const tabs = this.getAvailableTabs();
		const selectedTabs = pages?.home?.previewPanelTabs;
		const activeTabs = tabs.filter(tab => selectedTabs?.[tab.view] ?? tab.defaultActive);

		let tiles;
		if (state.activeTab === 'boltShowtimes') {
			if (state.showtimesLoading === 'loading') {
				tiles = <div class={this.s.showtimesLoader} />;
			} else {
				tiles = [
					<div class={this.s.showtimesCity}>{this.state.geo?.city}</div>,
					<div class={this.s.theaters}>
						{state.showtimes?.slice(0, 2).map(this.renderTheater)}
					</div>,
				];
			}
		} else if (/^boltHomeEnt:?/.test(state.activeTab)) {
			const sectionId = state.activeTab.split(':')[1];
			const sections = pages?.homeEnt?.sections;
			const activeSectionData = sectionId ? sections?.[sectionId] : sortObjectByOrder(sections)[0];
			const items = sortObjectByOrder(activeSectionData?.items, (value, id) => [id, value]).slice(0, 6);
			tiles = items.map(([id, item]) => {
				const image = this.getMerchantLogo(item);
				return (
					<div class={joinClasses(this.s.previewHomeEntTile)} key={id}>
						{image && <img src={image} alt="" />}
					</div>
				);
			});
		}
		return [
			<div class={this.s.previewToggleButtons}>
				{activeTabs.map(tab => (
					<button
						class={joinClasses(this.s.toggleBtn, state.activeTab === tab.view && this.s.toggleBtnActive)}
						onClick={this.onTabClick}
						data-tab={tab.view}
						data-section="#"
						key={tab.view}
					>
						{tab.name}
					</button>
				))}
			</div>,

			<div class={this.s.previewGridContainer} data-active-tab={state.activeTab} key="grid">
				{tiles}
			</div>,

			<div class={this.s.moreButton}>{state.activeTab === 'boltShowtimes' ? 'MORE SHOWTIMES' : 'MORE WAYS TO WATCH'}</div>,

			!!secondaryButton?.enabled && (
				<SelectableSection section="/pages/home/previewPanelSecondaryButton" {...props} class={this.s.secondaryBtn} key="button">{secondaryButton?.label}</SelectableSection>
			)
		];
	}

	async onResetSecondaryButtonClick() {
		const text = 'This will reset the secondary button to the default.';
		const ok = await confirm(text, { title: `Reset to default?`, danger: true, button: 'Reset to default' });
		if (!ok) return;
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/home/previewPanelSecondaryButton`, {
			label: '',
			path: '',
		});
	}

	renderSecondaryButtonSettings() {
		const dataRoot = this.props.dataRoot;
		const secondaryButtonData = this.movie?.pages?.home?.previewPanelSecondaryButton;
		const hasCustomData = secondaryButtonData && (secondaryButtonData.label || secondaryButtonData.path);

		return (
			<div class={this.s.itemEditor}>
				<SimpleField dark label="Secondary button text" path={`${dataRoot}/content/pages/home/previewPanelSecondaryButton/label`} />
				<LinkField
					dark
					label="Secondary button link"
					path={`${dataRoot}/content/pages/home/previewPanelSecondaryButton/path`}
					options={getPages(this.props.data)}
				/>
				<Button
					dark text danger
					class={this.s.resetButton}
					disabled={!hasCustomData}
					onClick={this.onResetSecondaryButtonClick}
				>
					Reset button
				</Button>
			</div>
		);
	}

	async onResetSectionsClick() {
		const text = 'This will reset the active sections to the default values.';
		const ok = await confirm(text, { title: 'Reset to default?', danger: true, button: 'Reset to default' });
		if (!ok) return;
		jsonPointer.removeDeep(Store.get().data, `${this.props.dataRoot}/content/pages/home/previewPanelTabs`);
	}

	renderPreviewPanelInfo() {
		return (
			<div class={this.s.itemGroup}>
				<h3 key="preview-panel-heading">Preview panel</h3>
				<p class={this.s.note}>Available options will change depending on the showtimes status and available Home Ent sections</p>
				<Button
					href={`#${this.props.config.basePath}/info`}
					class={this.s.releaseSettingsButton}
					dark outline
					iconAlt="chevron-right"
					iconSize="small"
				>
						Edit release settings
				</Button>
			</div>
		);
	}

	renderPreviewPanelSections() {
		const dataRoot = this.props.dataRoot;
		const tabs = this.getAvailableTabs();
		const hasNonDefaultTabs = Object.values(this.movie?.pages?.home?.previewPanelTabs || {}).some(v => v != undefined);

		if (!tabs.length) return;

		return (
			<div class={this.s.itemGroup}>
				<h3 key="sections-heading">Sections</h3>
				{tabs.map(tab => (
					<ToggleField
						dark
						class={this.s.toggle}
						path={`${dataRoot}/content/pages/home/previewPanelTabs/${tab.view}`}
						defaultValue={tab.defaultActive}
						key={tab.view}
					>
						{tab.name}
					</ToggleField>
				))}
				<div class={this.s.resetButton} key="reset-button">
					<Button
						dark text danger
						disabled={!hasNonDefaultTabs}
						onClick={this.onResetSectionsClick}
					>
						Reset sections
					</Button>
				</div>
			</div>
		);
	}

	renderPreviewPanelActionButtons() {
		const dataRoot = this.props.dataRoot;
		const secondaryButtonPath = `${dataRoot}/content/pages/home/previewPanelSecondaryButton`;
		const secondaryButton = jsonPointer.get(Store.get().data, secondaryButtonPath);

		return (
			<div class={this.s.itemGroup}>
				<h3>Secondary button</h3>
				<ToggleField dark small class={this.s.toggle} path={`${secondaryButtonPath}/enabled`}>
					<div class={this.s.item}>
						<div>{secondaryButton?.label || 'Secondary Button'}</div>
						<button
							class={this.s.edit}
							onClick={() => this.props.select('/pages/home/previewPanelSecondaryButton')}
							disabled={!jsonPointer.get(Store.get().data, `${secondaryButtonPath}/enabled`)}
						>
							Edit
						</button>
					</div>
				</ToggleField>
			</div>
		);
	}

	renderHeroContentSettings() {
		return (
			<div class={this.s.settingsWrapper}>
				{this.renderPreviewPanelInfo()}
				{this.renderPreviewPanelSections()}
				{this.renderPreviewPanelActionButtons()}
			</div>
		);
	}

	renderSettings() {
		const activeElement = this.props.activeElement;
		const nav = [
			{ label: 'Home', path: `/` },
			{ label: 'Hero', path: `/pages/home/hero` }
		];

		if (activeElement === '/pages/home/previewPanelSecondaryButton') {
			nav.push({ label: `Secondary button`, path: activeElement });

			return (
				<div class={this.s.settings}>
					{this.renderSettingsNavigation(nav)}
					{this.renderSecondaryButtonSettings()}
				</div>
			);
		}

		return super.renderSettings();
	}
}
